import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Checkbox } from "../../../formControls/Checkbox/Checkbox";
import { DatePicker } from "../../../formControls/DatePicker/DatePicker";
import { Select } from "../../../formControls/Select/Select";
import { Snackbar } from "../../../common/Snackbar/Snackbar";
import { Tooltip } from "../../../common/Tooltip/Tooltip";
import {
  APPOINTMENT_DATE_TOOLTIP_CONTENT,
  DEFAULT_TIME_FORMAT,
  TIMEZONES,
  TIMEZONE_TOOLTIP_CONTENT
} from "../../../../constants/constants";
import { DateHelper } from "../../../../utils";
import { ThirdStepTooltip } from "./ThirdStepTooltip";

import styles from "./index.module.css";

import { InfoIcon } from "../../../../assets";
import {
  DATE_TIME_FIELD_LABELS,
  DATE_TIME_LABEL_VARIANT,
  getEarliestPickUpLabel,
  getPickUpDateFieldLabel,
  getQuickScheduleDatePickerPopupContainer,
  isScheduleRideLayout,
  QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS,
} from './stepThreeDateTimeConstants';

export const PickUpDateTimeSection = ({
  handleUseWindowOfTime,
  handlePickUpDateTime,
  handlePickUpDateOnSelect,
  handlePickUpLowerLimitOnSelect,
  handleChangeTZ,
  pickUpDate,
  setFieldValue,
  appointmentDate,
  disabledDatesForPickUpDate,
  disabledDatesForAppointment,
  disabledDateTime,
  values,
  windowOfTimeIsValid,
  disabledValues,
  minDelayToStartRide,
  maxPickUpWindowOfTime,
  disabledDateForHigherLimit,
  disabledTimeForHigherLimit,
  handleChangePickUpDateToLowerLimit,
  pickUpDateLowerLimit,
  handleChangeappointmentDate,
  handleAppointmentDateCheckBox,
  showTitle = true,
  isUberRide = false,
  hideFlexibleCheckbox = false,
  hideAppointmentCheckbox = false,
  labelVariant = DATE_TIME_LABEL_VARIANT.DEFAULT,
  fieldInputClassName,
  selectFieldClassName,
}) => {
  const datePickerClass = fieldInputClassName || undefined;
  const selectClass = selectFieldClassName || fieldInputClassName || undefined;
  const [isAppointmentDateSelected, setIsAppointmentDateSelected] =
    useState(false);

  const handleChangeAppointmentCheckbox = (e) => {
    const isChecked = e.checked; // Safely access the checked property
    if (isChecked === undefined) {
      return;
    }

    setIsAppointmentDateSelected(isChecked);

    handleAppointmentDateCheckBox({
      name: e.name,
      checked: isChecked,
      setFieldValue, // Pass setFieldValue here
      values
    });
  };


  const minAvailablePickUpDate = useMemo(
    () =>
      DateHelper.addDates(
        DateHelper.utcToZonedTime(Date.now(), values?.pickUpDate?.timezone),
        minDelayToStartRide
      ),
    [values?.pickUpDate?.timezone, minDelayToStartRide]
  );

  const diabledDatesForPickUpField = useMemo(
    () =>
      values?.isPickUpDateFlexible
        ? disabledDateForHigherLimit
        : disabledDatesForPickUpDate(minAvailablePickUpDate),
    [
      values?.isPickUpDateFlexible,
      disabledDateForHigherLimit,
      disabledDatesForPickUpDate,
      minAvailablePickUpDate,
    ]
  );
  const diabledDatesForAppointmentField = useMemo(
    () =>
      values?.isPickUpDateFlexible
        ? disabledDateForHigherLimit
        : disabledDatesForAppointment(pickUpDate ? pickUpDate : minAvailablePickUpDate),
    [
      values?.isPickUpDateFlexible,
      disabledDateForHigherLimit,
      disabledDatesForAppointment,
      pickUpDate,
    ]
  );

  const disabledDateTimeForPickUpField = useMemo(
    () =>
      values?.isPickUpDateFlexible
        ? disabledTimeForHigherLimit
        : disabledDateTime(minAvailablePickUpDate, pickUpDate),
    [
      values?.isPickUpDateFlexible,
      disabledTimeForHigherLimit,
      disabledDateTime,
      pickUpDate,
      minAvailablePickUpDate,
    ]
  );

  const disabledDateTimeForAppointmentField = useMemo(
    () =>

      disabledDateTime(pickUpDate, appointmentDate),
    [
      pickUpDate,
      disabledDateTime,
      appointmentDate,
    ]
  );

  const scheduleRideLayout = isScheduleRideLayout(labelVariant);

  const datePickerPopupClassName = scheduleRideLayout
    ? QUICK_SCHEDULE_DATE_PICKER_POPUP_CLASS
    : undefined;

  const datePickerGetPopupContainer = scheduleRideLayout
    ? getQuickScheduleDatePickerPopupContainer
    : (trigger) => trigger;

  const pickUpDateFieldLabel = useMemo(
    () =>
      getPickUpDateFieldLabel({
        labelVariant,
        isPickUpDateFlexible: values?.isPickUpDateFlexible,
      }),
    [values?.isPickUpDateFlexible, labelVariant],
  );

  const earliestPickUpLabel = useMemo(
    () => getEarliestPickUpLabel(labelVariant),
    [labelVariant],
  );

  const pickUpDateFieldDisabled = useMemo(
    () =>
      !values?.pickUpDate?.timezone ||
      (values?.isPickUpDateFlexible && !pickUpDateLowerLimit) ||
      disabledValues?.pickUpDate,
    [
      values?.pickUpDate?.timezone,
      values?.isPickUpDateFlexible,
      pickUpDateLowerLimit,
      disabledValues?.pickUpDate,
    ]
  );

  const pickUpDefaultDateTime = useMemo(() => {
    if (minAvailablePickUpDate && DateHelper.isValidDateTime(minAvailablePickUpDate)) {
      return minAvailablePickUpDate;
    }
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }, [minAvailablePickUpDate]);

  const latestPickUpDefaultDateTime = useMemo(() => {
    if (
      values?.isPickUpDateFlexible &&
      pickUpDateLowerLimit &&
      DateHelper.isValidDateTime(pickUpDateLowerLimit)
    ) {
      return pickUpDateLowerLimit;
    }
    return pickUpDefaultDateTime;
  }, [values?.isPickUpDateFlexible, pickUpDateLowerLimit, pickUpDefaultDateTime]);

  const schedulePickerDefaults =
    scheduleRideLayout || handlePickUpDateOnSelect
      ? {
          alignPanelToDefault: scheduleRideLayout,
          hideDisabledOptions: false,
          needConfirm: true,
        }
      : {};

  const handlePickUpMainDateSelect = (date) => {
    if (!date || !DateHelper.isValidDateTime(date) || !handlePickUpDateOnSelect) return;
    handlePickUpDateOnSelect({ value: date, setFieldValue, values });
  };

  const handlePickUpEarliestDateSelect = (date) => {
    if (!date || !DateHelper.isValidDateTime(date) || !handlePickUpLowerLimitOnSelect) return;
    handlePickUpLowerLimitOnSelect({ value: date, setFieldValue, values });
  };

  useEffect(() => {
    const selectAppointmentDate = values?.appointmentDate?.timezone || values?.appointmentDate?.datetime ? true : false;
    
    setIsAppointmentDateSelected(selectAppointmentDate);

    return;
  }, [values?.appointmentDate]);

  return (
    <>
      {showTitle && (
        <h3 className={classNames(styles.subHeadings, styles.pickupSectionHeading)}>
          {DATE_TIME_FIELD_LABELS.PICKUP_SECTION_TITLE}
        </h3>
      )}

      {!hideFlexibleCheckbox && !(isUberRide && values?.isRoundtrip) && (
        <div className={styles.windowOfTime}>
          <label className={styles.useWindowOfTime}>
            <Checkbox
              name="isPickUpDateFlexible"
              checked={values?.isPickUpDateFlexible}
              onChange={handleUseWindowOfTime}
              disabled={disabledValues?.isPickUpDateFlexible}
              whenFieldDisabledShowTooltip={Boolean(
                disabledValues?.isPickUpDateFlexible
              )}
            />
            <span className={styles.checkboxLabelText}>
              {DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
            </span>
          </label>
          <ThirdStepTooltip
            range={maxPickUpWindowOfTime}
            useFlexibleHoursMessage
          />
        </div>
      )}

      <div
        className={classNames(styles.dateAndTime, {
          qsScheduleDateAndTime: scheduleRideLayout,
        })}
      >
        <label
          className={classNames({
            [styles.timezone]: !scheduleRideLayout,
            qsScheduleTimezone: scheduleRideLayout,
          })}
        >
          <span className={styles.required}>{DATE_TIME_FIELD_LABELS.TIMEZONE}</span>
          <div className={styles.timezoneSection}>
            <Select
              name="pickUpDate.timezone"
              options={TIMEZONES}
              onChange={handleChangeTZ}
              placeholder={"Pick one"}
              disabled={disabledValues?.pickUpDate}
              myClassName={selectClass}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.pickUpDate)}
            />
            {!scheduleRideLayout && (
              <div className={styles.timeZoneTooltip}>
                <Tooltip
                  title={
                    <span className={styles.tooltipContent}>
                      {TIMEZONE_TOOLTIP_CONTENT}
                    </span>
                  }
                >
                  <span className={styles.tooltipInfo}>
                    <InfoIcon width={30} height={30} className={styles.infoIcon} />
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        </label>

        <div
          className={classNames(styles.range, {
            qsScheduleRange: scheduleRideLayout,
          })}
        >
          {values?.isPickUpDateFlexible && (
            <label
              className={classNames(styles.startDate, {
                qsScheduleStartDate: scheduleRideLayout,
              })}
            >
              <span className={styles.required}>{earliestPickUpLabel}</span>

              <DatePicker
                name="pickUpDateLowerLimit.datetime"
                allowClear
                myClassName={datePickerClass}
                onChange={handleChangePickUpDateToLowerLimit}
                format={DEFAULT_TIME_FORMAT}
                value={
                  pickUpDateLowerLimit && DateHelper.isValidDateTime(pickUpDateLowerLimit)
                    ? pickUpDateLowerLimit
                    : ''
                }
                showTime={{ defaultValue: pickUpDefaultDateTime }}
                showNow={false}
                use12Hours
                defaultPickerValue={pickUpDefaultDateTime}
                disabledDate={disabledDatesForPickUpDate(
                  minAvailablePickUpDate
                )}
                disabledTime={disabledDateTime(
                  minAvailablePickUpDate,
                  pickUpDateLowerLimit
                )}
                disabled={
                  !values?.pickUpDate?.timezone || disabledValues?.pickUpDate
                }
                whenFieldDisabledShowTooltip={Boolean(
                  disabledValues?.pickUpDate
                )}
                inputReadOnly
                popupRootClassName={datePickerPopupClassName}
                getPopupContainer={datePickerGetPopupContainer}
                onSelect={handlePickUpEarliestDateSelect}
                {...schedulePickerDefaults}
              />
            </label>
          )}
          <label
            className={classNames({
              [styles.endDate]: values?.isPickUpDateFlexible,
              [styles.datepicker]: !values?.isPickUpDateFlexible,
              qsScheduleEndDate:
                scheduleRideLayout && values?.isPickUpDateFlexible,
              qsScheduleDatepicker:
                scheduleRideLayout && !values?.isPickUpDateFlexible,
            })}
          >
            <span className={styles.required}>{pickUpDateFieldLabel}</span>
            <DatePicker
              name="pickUpDate.datetime"
              allowClear
              myClassName={datePickerClass}
              onChange={handlePickUpDateTime}
              format={DEFAULT_TIME_FORMAT}
              value={pickUpDate && DateHelper.isValidDateTime(pickUpDate) ? pickUpDate : ''}
              showTime={{ defaultValue: latestPickUpDefaultDateTime }}
              showNow={false}
              use12Hours
              disabledDate={diabledDatesForPickUpField}
              disabledTime={disabledDateTimeForPickUpField}
              disabled={pickUpDateFieldDisabled}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.pickUpDate)}
              inputReadOnly
              defaultPickerValue={latestPickUpDefaultDateTime}
              popupRootClassName={datePickerPopupClassName}
              getPopupContainer={datePickerGetPopupContainer}
              onSelect={handlePickUpMainDateSelect}
              {...schedulePickerDefaults}
            />
          </label>
        </div>
      </div>

      {!windowOfTimeIsValid && (
        <div className={styles.snackbarField}>
          <Snackbar
            type="warning"
            title={DATE_TIME_FIELD_LABELS.INVALID_PICKUP_WINDOW_TITLE}
            content={DATE_TIME_FIELD_LABELS.INVALID_PICKUP_WINDOW_CONTENT}
          />
        </div>
      )}

      {!hideAppointmentCheckbox && (
        <>
          <h3 className={classNames(styles.subHeadings, styles.appointmentSectionHeading)}>
            {DATE_TIME_FIELD_LABELS.APPOINTMENT_DETAILS_TITLE}
          </h3>
          <div className={styles.windowOfAppointmentTime}>
            <label className={styles.useWindowOfTime}>
              <Checkbox
                name="showAppointmentDateTimeZone"
                checked={isAppointmentDateSelected}
                onChange={handleChangeAppointmentCheckbox}
                disabled={disabledValues?.appointmentDate}
              />
              <span className={styles.checkboxLabelText}>
                {DATE_TIME_FIELD_LABELS.APPOINTMENT_CHECKBOX}
              </span>
            </label>
          </div>
        </>
      )}

      {!hideAppointmentCheckbox && isAppointmentDateSelected && (
        <div className={styles.dateAndTime}>
          <label className={styles.appointmentDateField}>
            <span>{DATE_TIME_FIELD_LABELS.APPOINTMENT_DATE_TIME}</span>
            <div className={styles.appointmentWrapper}>
              <div className={styles.appointmentDatePickerCol}>
                <DatePicker
                  name="appointmentDate.datetime"
                  allowClear
                  myClassName={datePickerClass}
                  placeholder={DATE_TIME_FIELD_LABELS.APPOINTMENT_PLACEHOLDER}
                  onChange={handleChangeappointmentDate}
                  format={DEFAULT_TIME_FORMAT}
                  value={appointmentDate}
                  showTime={{ defaultValue: pickUpDate }}
                  showNow={false}
                  use12Hours
                  disabled={disabledValues?.appointmentDate || !pickUpDate}
                  disabledDate={diabledDatesForAppointmentField}
                  disabledTime={disabledDateTimeForAppointmentField}
                  inputReadOnly
                  popupRootClassName={datePickerPopupClassName}
                  getPopupContainer={datePickerGetPopupContainer}
                />
              </div>
              <div className={styles.appointmentDate}>
                <Tooltip
                  title={
                    <span className={styles.tooltipContent}>
                      {APPOINTMENT_DATE_TOOLTIP_CONTENT}
                    </span>
                  }
                >
                  <span className={styles.tooltipInfo}>
                    <InfoIcon width={30} height={30} className={styles.infoIcon} />
                  </span>
                </Tooltip>
              </div>
            </div>
          </label>
        </div>
      )}
    </>
  );
};
